import { FatalAttraction as FA } from '@vpr/tracking';

import { Event, BigPictureClient, Fields } from '@vimeo/bigpicture-client';
import VimeoAuthFlowActionV12 from '@vimeo/bigpicture-client/types/events/vimeo.auth_flow_action/12';
import VimeoAuthFlowActionV13 from '@vimeo/bigpicture-client/types/events/vimeo.auth_flow_action/13';
import {
  buildActionBpContext,
  buildProductAnalyticsBpContext,
  buildThirdPartyIntegrationBpContext,
  buildWebBpContext,
  ProductAnalyticsBpContext,
  sendBpEventWithContexts,
  WebBpContext,
} from '@vimeo/use-tracking';
import { Viewer } from '@vimeo/viewer';

import { getPageNameFromPath, getPathName } from '../F2PJoinLogin/utils';
import getDeviceType from '../utils/getDeviceType';

const AUTH_TYPE_JOIN = 'join';
const AUTH_TYPE_LOGIN = 'login';
const ACTION_TYPE_CLICK = 'click';
const ACTION_TYPE_IMPRESSION = 'impression';
const ACTION_TYPE_DISMISS = 'dismiss';
const ACTION_TYPE_SELECT = 'select';
const ACTION_TYPE_PAGEVIEW = 'pageview';
const LOCATION_JOIN_MODAL = 'join_modal';
const LOCATION_LOGIN_MODAL = 'login_modal';
const REG_V2_SCHEMA_NAME = 'vimeo.auth_flow_action';
const REG_V2_VERSION = 11;

type Location = 'review_page' | 'folder_share_header' | 'creator_card' | 'header';

interface FatalAttraction {
  container: string;
  thirdPartyIntegration?: string;
}

const allShaered = {
  client_ver: null,
  debug_mode: null,
  in_background: null,
  flow: null,
  vsid: null,
  client_timestamp: null,
};

const CREATE_HOMEPAGE_LOCATION = 'create_homepage';

type Method = 'password' | 'google' | 'facebook' | 'apple';

export const trackLogin = (method: Method, fa?: FatalAttraction) => {
  if (fa) {
    FA.trackClick({
      container: fa.container,
      component: 'login',
      keyword: JSON.stringify({
        mode: method === 'password' ? 'email' : method,
        third_party_integration: fa?.thirdPartyIntegration || 'none',
      }),
    });
  }
  BigPictureClient.sendEvent(
    new Event('login_from_login_modal', 4, {
      product: 'Vimeo Create',
      path: window.location.pathname,
      location: CREATE_HOMEPAGE_LOCATION,
      method,
      test_layout: null,
      via: fa?.container || null,
      third_party_integration: fa?.thirdPartyIntegration || 'none',
    }),
  );
};

export const trackRegistration = (method: Method, fa?: FatalAttraction, via?: string) => {
  if (fa) {
    FA.trackClick({
      container: fa.container,
      component: 'join',
      keyword: JSON.stringify({
        mode: method === 'password' ? 'email' : method,
        third_party_integration: fa?.thirdPartyIntegration || 'none',
      }),
    });
  }
  BigPictureClient.sendEvent(
    new Event('register_from_login_modal', 4, {
      product: 'Vimeo Create',
      path: window.location.pathname,
      location: CREATE_HOMEPAGE_LOCATION,
      method,
      test_layout: null,
      via: fa?.container || via || null,
      third_party_integration: fa?.thirdPartyIntegration || 'none',
    }),
  );
};

export const trackViewSignupLoginScreen = (
  faContainer?: string,
  viewer?: Viewer,
  showFBCreativeConnectJoinModal?: boolean,
  type?: 'login' | 'join',
) => {
  const ALL_BI_LOGIN_FIELDS = {
    product: 'Vimeo Create',
    path: window.location.pathname,
    location: CREATE_HOMEPAGE_LOCATION,
    session_id: (viewer?.xsrft && parseInt(viewer.xsrft)) || null,
    platform: window.navigator.platform,
    user_id: viewer?.user?.id.toString() || null,
    user_package_type: JSON.stringify(viewer?.user?.account) || null,
    third_party_integration: showFBCreativeConnectJoinModal ? 'Facebook' : 'none',
    clients_session_start_ts: null,
    is_guest: viewer ? !viewer.user : null,
    device_id: viewer?.vuid || null,
    lang: viewer?.locale || null,
    via: faContainer || null,
    test_layout: null,
    join_or_login: type || null,
    referrer: CREATE_HOMEPAGE_LOCATION,
    ...allShaered,
  };
  BigPictureClient.sendEvent(
    new Event('vimeo.view_signup_login_screen', 2, {
      ...ALL_BI_LOGIN_FIELDS,
    }),
  );
};

export const clickToCloseSignupLoginPpModal = (
  faContainer?: string,
  viewer?: Viewer | null,
  showFBCreativeConnectJoinModal?: boolean,
  type?: 'login' | 'join',
  container?: string,
) => {
  const ALL_BI_LOGIN_FIELDS = {
    product: 'Vimeo Create',
    path: window.location.pathname,
    location: container || '',
    session_id: (viewer?.xsrft && parseInt(viewer?.xsrft)) || null,
    platform: window.navigator.platform,
    user_id: viewer?.user?.id.toString() || null,
    user_package_type: JSON.stringify(viewer?.user?.account) || null,
    third_party_integration: showFBCreativeConnectJoinModal ? 'Facebook' : 'none',
    device_id: viewer?.vuid || null,
    clients_session_start_ts: null,
    is_guest: viewer ? !viewer.user : null,
    lang: viewer?.locale || null,
    via: faContainer || null,
    test_layout: null,
    join_or_login: type || null,
    ...allShaered,
  };
  BigPictureClient.sendEvent(
    new Event('vimeo.click_to_close_signup_login_screen', 1, {
      ...ALL_BI_LOGIN_FIELDS,
    }),
  );
};

export const viewPricingPage = (viewer?: Viewer, showFBCreativeConnectJoinModal?: boolean) => {
  const ALL_BI_LOGIN_FIELDS = {
    product: 'Vimeo Create',
    path: window.location.pathname,
    location: CREATE_HOMEPAGE_LOCATION,
    session_id: (viewer?.xsrft && parseInt(viewer?.xsrft)) || null,
    platform: window.navigator.platform,
    user_id: JSON.stringify(viewer?.user?.id) || null,
    user_package_type: JSON.stringify(viewer?.user?.account) || null,
    third_party_integration: showFBCreativeConnectJoinModal ? 'Facebook' : 'none',
    device_id: viewer?.vuid || null,
    clients_session_start_ts: null,
    is_guest: viewer ? !viewer.user : null,
    lang: viewer?.locale || null,
    ...allShaered,
  };
  BigPictureClient.sendEvent(
    new Event('vimeo.view_pricing_page', 2, {
      ...ALL_BI_LOGIN_FIELDS,
    }),
  );
};

export const trackClickGOT = (viewer?: Viewer | null) => {
  BigPictureClient.sendEvent(
    new Event('vimeo.click_on_google_one_tap', 1, {
      product: 'Vimeo Create',
      path: window.location.pathname,
      location: CREATE_HOMEPAGE_LOCATION,
      session_id: (viewer?.xsrft && parseInt(viewer.xsrft)) || null,
      client_timestamp: Date.now().toString(),
      platform: window.navigator.platform,
      user_id: JSON.stringify(viewer?.user?.id) || null,
      user_package_type: JSON.stringify(viewer?.user?.account) || null,
      third_party_integration: 'none',
      vsid: null,
      via: CREATE_HOMEPAGE_LOCATION,
    }),
  );
};

interface Props {
  target: string;
  copy: string;
  name: string;
  location: Fields<'vimeo.click', 102>['location'];
}

export const sendF2pModalClickAnalytics = ({ target, copy, name, location }: Props) => {
  const path = getPathName();
  BigPictureClient.sendEvent(
    new Event('vimeo.click', 102, {
      feature: 'reg_flow_web',
      type: 'general',
      click_type: 'click',
      target_path: null,
      third_party_integration: null,
      name,
      target,
      copy,
      location,
      page: 'sign_up_modal',
      device_type: getDeviceType(),
      path,
    }),
  );
};

/*  Reg Flow V2 BP events */
export function getCommonAttributesV2(isRegV3Variant?: boolean) {
  const interface_version = isRegV3Variant ? 'reg_flow_3_0' : 'reg_flow_2_0';
  const path = getPathName();
  const pageName = getPageNameFromPath(path);
  // page is enum and pathname could be anything so adding other so that events are validated
  const page =
    pageName === path
      ? 'other'
      : (pageName as Fields<typeof REG_V2_SCHEMA_NAME, typeof REG_V2_VERSION>['page']);
  return {
    interface_version,
    interface_type: 'modal',
    page,
    path,
    referrer: document.referrer,
    referrer_path: document.referrer ? document.referrer.replace(/^.*\/\/[^\/]+/, '') : null,
    device_type: getDeviceType(),
    third_party_integration: null,
  } as const;
}

// When the join modal appears
export const trackJoinModalImpression = (isRegV3Variant?: boolean) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'join_modal_impression',
      auth_type: AUTH_TYPE_JOIN,
      action_type: ACTION_TYPE_IMPRESSION,
      is_final_action: false,
      location: isRegV3Variant ? `${LOCATION_JOIN_MODAL}_step_1` : LOCATION_JOIN_MODAL,
      copy: null,
      target: null,
      target_path: null,
      feature: 'join',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

export const trackJoinPageImpressionRegFlow0625 = (fields?: Partial<VimeoAuthFlowActionV12>) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, 12, {
      path: getPathName(),
      referrer: document.referrer,
      referrer_path: document.referrer ? document.referrer.replace(/^.*\/\/[^\/]+/, '') : null,
      is_final_action: false,
      interface_version: 'reg_flow_0625_v2',
      third_party_integration: null,
      auth_type: AUTH_TYPE_JOIN,
      interface_type: 'page',
      action_type: ACTION_TYPE_PAGEVIEW,
      device_type: getDeviceType(),
      feature: 'join',
      location: 'join_page_step_1',
      page: 'reg_flow_page',
      event_name: 'join_pageview',
      copy: null,
      target: null,
      target_path: null,
      ...fields,
    }),
  );
};

export const trackJoinPageClickRegFlow0625 = (fields?: Partial<VimeoAuthFlowActionV13>) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, 13, {
      path: getPathName(),
      referrer: document.referrer,
      referrer_path: document.referrer ? document.referrer.replace(/^.*\/\/[^\/]+/, '') : null,
      is_final_action: false,
      interface_version: 'reg_flow_0625_v2',
      third_party_integration: null,
      auth_type: AUTH_TYPE_JOIN,
      interface_type: 'page',
      action_type: ACTION_TYPE_CLICK,
      device_type: getDeviceType(),
      feature: 'join',
      location: 'join_page_step_1',
      page: 'reg_flow_page',
      event_name: '',
      copy: null,
      target: null,
      target_path: null,
      password_strength_score: null,
      ...fields,
    }),
  );
};

export const trackMarketingTermCheckboxRegFlow0625 = (checked: boolean) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, 12, {
      path: getPathName(),
      referrer: document.referrer,
      referrer_path: document.referrer ? document.referrer.replace(/^.*\/\/[^\/]+/, '') : null,
      is_final_action: false,
      interface_version: 'reg_flow_0625_v2',
      third_party_integration: null,
      interface_type: 'page',
      auth_type: AUTH_TYPE_JOIN,
      action_type: ACTION_TYPE_SELECT,
      device_type: getDeviceType(),
      location: 'join_page_step_2',
      page: 'reg_flow_page',
      event_name: checked ? 'check_marketing_optin' : 'uncheck_marketing_optin',
      copy: 'i_agree_to_receive_newsletters,_product_updates_and_exclusive_offers_from_vimeo.',
      target: null,
      target_path: null,
      feature: 'join',
    }),
  );
};

// When the login option within join modal is clicked
export const trackLoginWithinJoinModalClick = (isRegV3Variant: boolean) => {
  const copy = isRegV3Variant
    ? 'already_have_an_account?_log_in'
    : 'create_and_share_videos_in_seconds._join_or_log_in';

  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'join_or_log_in',
      auth_type: AUTH_TYPE_JOIN,
      action_type: ACTION_TYPE_CLICK,
      is_final_action: false,
      location: LOCATION_JOIN_MODAL,
      copy,
      target: LOCATION_LOGIN_MODAL,
      target_path: null,
      feature: 'join',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

// When marketing term checkbox is checked or unchecked
export const trackMarketingTermCheckbox = (checked: boolean, isRegV3Variant?: boolean) => {
  const event_name = checked ? 'check_marketing_optin' : 'uncheck_marketing_optin';
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name,
      auth_type: AUTH_TYPE_JOIN,
      action_type: ACTION_TYPE_SELECT,
      is_final_action: false,
      location: isRegV3Variant ? `${LOCATION_JOIN_MODAL}_step_2` : LOCATION_JOIN_MODAL,
      copy: 'i_agree_to_receive_newsletters,_product_updates_and_exclusive_offers_from_vimeo.',
      target: null,
      target_path: null,
      feature: 'join',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

// When the join with email is clicked within join modal is clicked
export const trackJoinWithEmailClick = (isRegV3Variant?: boolean, redirectUrl?: string) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'join_with_email',
      auth_type: AUTH_TYPE_JOIN,
      action_type: ACTION_TYPE_CLICK,
      is_final_action: true,
      location: isRegV3Variant ? `${LOCATION_JOIN_MODAL}_step_2` : LOCATION_JOIN_MODAL,
      copy: 'join_with_email',
      target: 'email_auth_join',
      target_path: redirectUrl ? redirectUrl : null,
      feature: 'join',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

// When the join with email is clicked within join modal is clicked
export const trackContinueWithEmailClick = () => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'join_with_email',
      auth_type: AUTH_TYPE_JOIN,
      action_type: ACTION_TYPE_CLICK,
      is_final_action: false,
      location: `${LOCATION_JOIN_MODAL}_step_1`,
      copy: 'continue_with_email',
      target: 'email_auth_join',
      target_path: null,
      feature: 'join',
      ...getCommonAttributesV2(true),
    }),
  );
};

// When the join with google within join modal is clicked
export const trackJoinWithGoogleClick = (isRegV3Variant?: boolean, redirectUrl?: string | null) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'join_with_google',
      auth_type: AUTH_TYPE_JOIN,
      action_type: ACTION_TYPE_CLICK,
      is_final_action: true,
      location: isRegV3Variant ? `${LOCATION_JOIN_MODAL}_step_1` : LOCATION_JOIN_MODAL,
      copy: 'join_with_google',
      target: 'google_auth_join',
      target_path: redirectUrl ? redirectUrl : null,
      feature: 'join',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

// When the join modal gets closed
export const trackJoinModalDismiss = (isF2PVariant: boolean, isMobile?: boolean) => {
  let eventName = isMobile ? 'go_back' : 'dismiss_join_modal';
  let copy = isMobile ? 'go_back' : null;
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: eventName,
      auth_type: AUTH_TYPE_JOIN,
      action_type: ACTION_TYPE_DISMISS,
      is_final_action: true,
      location: LOCATION_JOIN_MODAL,
      copy,
      target: null,
      target_path: null,
      feature: 'join',
      ...getCommonAttributesV2(isF2PVariant),
    }),
  );
};

// When the login modal appears
export const trackLoginModalImpression = (isRegV3Variant?: boolean) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'login_modal_impression',
      auth_type: AUTH_TYPE_LOGIN,
      action_type: ACTION_TYPE_IMPRESSION,
      is_final_action: false,
      location: LOCATION_LOGIN_MODAL,
      copy: null,
      target: null,
      target_path: null,
      feature: 'login',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

// When the join option within join modal is clicked
export const trackJoinWithinLoginModalClick = (isRegV3Variant: boolean) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'join_or_log_in',
      auth_type: AUTH_TYPE_LOGIN,
      action_type: ACTION_TYPE_CLICK,
      is_final_action: false,
      location: LOCATION_LOGIN_MODAL,
      copy: 'don’t_have_an_account?_join_vimeo',
      target: LOCATION_JOIN_MODAL,
      target_path: null,
      feature: 'login',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

// When the forgot password option within login modal is clicked
export const trackForgotPasswordClick = (isRegV3Variant?: boolean, redirectUrl?: string | null) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'forgot_your_password',
      auth_type: AUTH_TYPE_LOGIN,
      action_type: ACTION_TYPE_CLICK,
      is_final_action: false,
      location: LOCATION_LOGIN_MODAL,
      copy: 'forgot_your_password?',
      target: 'forgot_password',
      target_path: redirectUrl ? redirectUrl : null,
      feature: 'login',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

// When 'continue' button is clicked within login modal to login with email
export const trackLoginWithEmailClick = (isRegV3Variant?: boolean, redirectUrl?: string | null) => {
  const copy = 'log_in_with_an_email';
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'login_with_email',
      auth_type: AUTH_TYPE_LOGIN,
      action_type: ACTION_TYPE_CLICK,
      is_final_action: true,
      location: LOCATION_LOGIN_MODAL,
      copy,
      target: 'email_auth_login',
      target_path: redirectUrl ? redirectUrl : null,
      feature: 'login',
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

// When the login with google or facebook or apple within login modal is clicked
export const trackLoginWithSocialMediaClick = (
  socialMedia: string,
  isRegV3Variant?: boolean,
  redirectUrl?: string | null,
) => {
  const commonAttributes =
    socialMedia === 'google'
      ? {
          event_name: 'login_with_google',
          copy: 'log_in_with_google',
          target: 'google_auth_login',
        }
      : socialMedia === 'facebook'
        ? {
            event_name: 'login_with_facebook',
            copy: 'log_in_with_facebook',
            target: 'facebook_auth',
          }
        : {
            event_name: 'login_with_apple',
            copy: 'log_in_with_apple',
            target: 'apple_auth',
          };

  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      auth_type: AUTH_TYPE_LOGIN,
      action_type: ACTION_TYPE_CLICK,
      is_final_action: true,
      location: LOCATION_LOGIN_MODAL,
      target_path: redirectUrl ? redirectUrl : null,
      feature: 'login',
      ...commonAttributes,
      ...getCommonAttributesV2(isRegV3Variant),
    }),
  );
};

export const trackLoginModalDismiss = (isF2PVariant: boolean, isMobile?: boolean) => {
  let eventName = isMobile ? 'go_back' : 'dismiss_login_modal';
  let copy = isMobile ? 'go_back' : null;
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: eventName,
      auth_type: AUTH_TYPE_LOGIN,
      action_type: ACTION_TYPE_DISMISS,
      is_final_action: true,
      location: LOCATION_LOGIN_MODAL,
      copy,
      target: null,
      target_path: null,
      feature: 'login',
      ...getCommonAttributesV2(isF2PVariant),
    }),
  );
};

export const trackGoBackToPrevScreenCTAClick = () => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: 'previous_step',
      auth_type: AUTH_TYPE_JOIN,
      action_type: 'click',
      is_final_action: false,
      location: `${LOCATION_JOIN_MODAL}_step_2`,
      copy: 'go_back',
      target: null,
      target_path: null,
      feature: 'reg_flow',
      ...getCommonAttributesV2(true),
    }),
  );
};

export const trackJoinLoginCTAClick = (
  authType: 'join' | 'login',
  location: Location,
  copy: string,
) => {
  BigPictureClient.sendEvent(
    new Event(REG_V2_SCHEMA_NAME, REG_V2_VERSION, {
      event_name: `click_${authType}`,
      auth_type: authType,
      action_type: 'trigger',
      is_final_action: false,
      location,
      copy,
      target: null,
      target_path: null,
      feature: 'reg_flow',
      ...getCommonAttributesV2(),
      interface_type: null,
      interface_version: null,
    }),
  );
};

export interface BpData {
  feature?: ProductAnalyticsBpContext['feature'];
  product?: ProductAnalyticsBpContext['product'];
  pageName?: WebBpContext['page_name'];
}

const buildContexts = ({
  copy,
  flow,
  pageName = 'other',
  product = 'general',
  feature = 'general',
}: {
  copy: string;
  flow: 'login' | 'join';
} & BpData) => {
  return {
    ...buildActionBpContext({
      action_type: 'click',
      feature: null,
    }),
    ...buildWebBpContext({
      page_name: pageName,
      path: window.location.pathname,
    }),
    ...buildProductAnalyticsBpContext({
      product,
      feature,
      location: 'modal',
      modal_name: 'join_modal',
      flow,
      element: 'modal',
      device_type: getDeviceType(),
      is_user_facing_data: null,
      copy,
    }),
    ...buildThirdPartyIntegrationBpContext({
      is_partner: false,
      integration_id: null,
      integration_name: null,
    }),
  };
};

export const trackTriggerAuthFlow = ({
  copy,
  flow,
  pageName = 'other',
  product = 'general',
  feature = 'general',
}: {
  copy: string;
  flow: 'login' | 'join';
} & BpData) => {
  sendBpEventWithContexts(
    'vimeo.trigger_auth_flow',
    buildContexts({ copy, flow, pageName, product, feature }),
    2,
    {
      interface_type: 'modal',
      auth_type: flow,
    },
  );
};

export const trackProgressInAuthFlow = ({
  copy,
  pageName = 'other',
  product = 'general',
  feature = 'general',
}: {
  copy: string;
} & BpData) => {
  sendBpEventWithContexts(
    'vimeo.progress_in_auth_flow',
    buildContexts({ copy, flow: 'join', pageName, product, feature }),
    2,
    {
      interface_type: 'modal',
      auth_type: 'join',
    },
  );
};

export const trackFinishAuthFlow = ({
  pageName = 'other',
  product = 'general',
  feature = 'general',
}: BpData) => {
  sendBpEventWithContexts(
    'vimeo.finish_auth_flow',
    buildContexts({ copy: 'join vimeo', flow: 'join', pageName, product, feature }),
    2,
    {
      interface_type: 'modal',
      auth_type: 'join',
    },
  );
};
